// Location: src/features/devices/components/DeviceEdit.tsx

import {
  Edit,
  SimpleForm,
  SelectInput,
  TextInput,
  ReferenceInput,
  DateField,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  FunctionField,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Devices as DeviceIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Storage as MetadataIcon,
} from '@mui/icons-material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import { validateHotelId } from '../validators/deviceValidators';
import {
  getDeviceTypeColor,
  getDeviceSourceColor,
  getDeviceStatusColor,
  getDeviceDisplayName,
} from '@/types/models';
import type { Device } from '@/types/models';
import { transformDeviceForEdit } from '../config/deviceCategories';
import { humanizeMetadataKey } from '../config/metadataKeys';
import { DeviceLabelsCard } from '@/features/labels/DeviceLabelsCard';

/**
 * Single info row for the device detail card
 */
interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Box>{children}</Box>
  </Box>
);

/**
 * Device identity header with key info at a glance
 */
const DeviceHeader = () => {
  const translate = useTranslate();
  const record = useRecordContext<Device & { category?: string }>();
  const theme = useTheme();

  if (!record) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: 2,
        backgroundColor: `${theme.palette.primary.main}10`,
        color: theme.palette.primary.main,
        flexShrink: 0,
      }}>
        <DeviceIcon sx={{ fontSize: 28 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {getDeviceDisplayName(record)}
        </Typography>
        {record.category && (
          <Typography variant="body2" color="text.secondary">
            {translate(`resources.devices.categories.${record.category}`)}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={translate(`resources.devices.enums.deviceType.${record.deviceType.toLowerCase()}`)}
          color={getDeviceTypeColor(record.deviceType)}
          size="small"
        />
        <Chip
          label={translate(`resources.devices.enums.status.${record.status.toLowerCase()}`)}
          color={getDeviceStatusColor(record.status)}
          size="small"
        />
        <Chip
          label={translate(`resources.devices.enums.source.${record.source.toLowerCase()}`)}
          color={getDeviceSourceColor(record.source)}
          size="small"
          variant="outlined"
        />
      </Box>
    </Paper>
  );
};

/**
 * Read-only device information card
 */
const DeviceInfoCard = () => {
  const translate = useTranslate();
  const record = useRecordContext<Device>();

  if (!record) return null;

  return (
    <MyFormSection
      title={translate('resources.devices.sections.readOnlyInfo')}
      icon={<InfoIcon />}
      variant="outlined"
    >
      <Grid container spacing={0}>
        <Grid item xs={12} sm={6}>
          <InfoRow label={translate('resources.devices.fields.registeredAt')}>
            <DateField source="registeredAt" showTime />
          </InfoRow>
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoRow label={translate('resources.devices.fields.lastSeenAt')}>
            <FunctionField
              render={(rec: Device) => {
                if (!rec.lastSeenAt) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {translate('common.never')}
                    </Typography>
                  );
                }
                return <DateField source="lastSeenAt" showTime />;
              }}
            />
          </InfoRow>
        </Grid>

      </Grid>
    </MyFormSection>
  );
};

/**
 * Metadata display card - shows all key/value pairs
 */
const MetadataCard = () => {
  const translate = useTranslate();
  const record = useRecordContext<Device>();

  if (!record) return null;

  const entries = record.metadata ? Object.entries(record.metadata) : [];

  return (
    <MyFormSection
      title={translate('resources.devices.fields.metadata')}
      icon={<MetadataIcon />}
      variant="outlined"
    >
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
          {translate('resources.devices.messages.noMetadata', { _: 'No metadata' })}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {entries.map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.75,
                px: 1.5,
                borderRadius: 1,
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {humanizeMetadataKey(key, translate)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {String(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </MyFormSection>
  );
};

/**
 * Device Edit Component - Redesigned layout
 *
 * Desktop: Header + two-column layout (info/metadata left, editable right)
 * Mobile: Single column, all sections stacked
 */
export const DeviceEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const handleCancel = () => navigate('/admin-options?tab=devices');

  const onSuccess = () => {
    notify('resources.devices.messages.updateSuccess', { type: 'success' });
    redirect('/admin-options?tab=devices');
  };

  const onError = (error: { body?: { error?: { message?: string } }; message?: string }) => {
    const errorMessage = error?.body?.error?.message ||
                        error?.message ||
                        translate('resources.devices.messages.updateError');
    notify(errorMessage, { type: 'error' });
  };

  const transform = (data: Record<string, unknown>) => {
    return transformDeviceForEdit(data);
  };

  return (
    <Edit
      mutationOptions={{ onSuccess, onError }}
      redirect="list"
      title={translate('resources.devices.edit.title')}
      mutationMode="pessimistic"
      transform={transform}
      queryOptions={{
        meta: { transform }
      }}
    >
      <SimpleForm
        toolbar={<MyToolbar resource="devices" onCancel={handleCancel} />}
        mode="onBlur"
        reValidateMode="onBlur"
        resetOptions={{ keepDirtyValues: true }}
      >
        <MyFormContainer maxWidth="lg">
          {/* Header: device identity at a glance */}
          <DeviceHeader />

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Left column: read-only info + metadata */}
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <DeviceInfoCard />
                  <DeviceLabelsCard />
                  <MetadataCard />
                </Box>
              </Grid>

              {/* Right column: editable fields */}
              <Grid item xs={12} md={7}>
                <MyFormSection
                  title={translate('resources.devices.sections.editableFields')}
                  subtitle={translate('resources.devices.sections.editableSubtitle')}
                  icon={<SettingsIcon />}
                  variant="outlined"
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Hotel */}
                  <ReferenceInput
                    source="hotelId"
                    reference="hotels"
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <SelectInput
                      label={translate('resources.devices.fields.hotel')}
                      optionText="name"
                      validate={[required(), validateHotelId]}
                      fullWidth
                      helperText={translate('resources.devices.hints.changeHotel')}
                    />
                  </ReferenceInput>

                  {/* Serial Number */}
                  <TextInput
                    source="serialNumber"
                    label={translate('resources.devices.fields.serialNumber')}
                    fullWidth
                    helperText={translate('resources.devices.hints.serialNumber')}
                  />

                  {/* Name (optional) */}
                  <TextInput
                    source="name"
                    label={translate('resources.devices.fields.name')}
                    fullWidth
                    helperText={translate('resources.devices.hints.name')}
                  />

                  {/* Status */}
                  <SelectInput
                    source="status"
                    label={translate('resources.devices.fields.status')}
                    choices={[
                      { id: 'Active', name: translate('resources.devices.enums.status.active') },
                      { id: 'Inactive', name: translate('resources.devices.enums.status.inactive') },
                      { id: 'PendingActivation', name: translate('resources.devices.enums.status.pendingActivation') },
                    ]}
                    fullWidth
                    helperText={translate('resources.devices.hints.changeStatus')}
                  />
                  </Box>
                </MyFormSection>
              </Grid>
            </Grid>
          </Box>
        </MyFormContainer>
      </SimpleForm>
    </Edit>
  );
};

export default DeviceEdit;
